import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table, Button, Input, Select, Modal, Form, Checkbox, Upload, Tag, Space,
  message, Popconfirm, Typography, Empty, Card, Progress,
} from 'antd';
import type { GetProp, UploadProps } from 'antd';
import {
  PlusOutlined, SearchOutlined, PaperClipOutlined, PushpinFilled,
  DownloadOutlined, DeleteOutlined, EditOutlined, ReloadOutlined, EyeOutlined,
} from '@ant-design/icons';
import { boardAPI } from '../services/api';
import RichTextEditor from '../components/RichTextEditor';
import { usePermission } from '../contexts/PermissionContext';
import { useFitTableHeight } from '../hooks/useViewportFit';
import { sanitizeRichHtml } from '../utils/html';

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];
type UploadStatus = 'ready' | 'uploading' | 'done' | 'error';
interface PendingFile {
  uid: string; file: File; name: string; size: number; percent: number; status: UploadStatus;
}

const { Text } = Typography;
const MAX_FILE_MB = 50;
const CATEGORIES = ['일반', '공지', '자료실', '기타'];

interface Attachment {
  id: number; file_name: string; file_type: string; file_size: number; uploaded_at?: string;
}
interface BoardItem {
  id: number; category: string; title: string; is_pinned: boolean;
  view_count: number; author_name: string; attachment_count: number;
  created_at?: string; updated_at?: string;
}
interface BoardDetail extends BoardItem {
  content: string; attachments: Attachment[];
}

const fmtSize = (n: number) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB']; let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};
const fmtDate = (s?: string) => (s ? s.replace('T', ' ').slice(0, 16) : '-');

const BoardPage: React.FC = () => {
  const { hasPermission } = usePermission();
  const canWrite = hasPermission('board', 'WRITE');
  const canDelete = hasPermission('board', 'DELETE');
  const { tableWrapRef, cardWrapRef, bodyHeight } = useFitTableHeight();

  const [data, setData] = useState<BoardItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>('전체');

  // 상세 모달
  const [detail, setDetail] = useState<BoardDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // 작성/수정 모달
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const [content, setContent] = useState('');
  const [existingAtts, setExistingAtts] = useState<Attachment[]>([]);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [saving, setSaving] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await boardAPI.list({
        search: search || undefined,
        category: category !== '전체' ? category : undefined,
        page, page_size: pageSize,
      });
      setData(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      message.error('목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [search, category, page, pageSize]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openDetail = async (id: number) => {
    try {
      const res = await boardAPI.get(id);
      setDetail(res.data);
      setDetailOpen(true);
    } catch {
      message.error('게시글을 불러오지 못했습니다.');
    }
  };

  const openCreate = () => {
    setEditId(null);
    form.resetFields();
    form.setFieldsValue({ category: '일반', is_pinned: false });
    setContent('');
    setExistingAtts([]);
    setPendingFiles([]);
    setFormOpen(true);
  };

  const openEdit = (d: BoardDetail) => {
    setEditId(d.id);
    form.setFieldsValue({ title: d.title, category: d.category, is_pinned: d.is_pinned });
    setContent(d.content || '');
    setExistingAtts(d.attachments || []);
    setPendingFiles([]);
    setDetailOpen(false);
    setFormOpen(true);
  };

  const beforeUpload = (file: FileType) => {
    const sizeMb = (file.size || 0) / 1024 / 1024;
    if (sizeMb > MAX_FILE_MB) {
      message.error(`'${file.name}' — 개별 파일은 ${MAX_FILE_MB}MB를 초과할 수 없습니다.`);
      return Upload.LIST_IGNORE;
    }
    setPendingFiles((prev) => [...prev, {
      uid: file.uid, file: file as unknown as File, name: file.name,
      size: file.size || 0, percent: 0, status: 'ready',
    }]);
    return false; // 자동 업로드 방지 — 저장 시 진행률 표시하며 수동 업로드
  };

  const removePending = (uid: string) =>
    setPendingFiles((prev) => prev.filter((p) => p.uid !== uid));

  const removeExistingAtt = async (attId: number) => {
    try {
      await boardAPI.deleteAttachment(attId);
      setExistingAtts((prev) => prev.filter((a) => a.id !== attId));
      message.success('첨부파일을 삭제했습니다.');
    } catch {
      message.error('첨부파일 삭제에 실패했습니다.');
    }
  };

  const handleSave = async () => {
    let values: any;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      let boardId = editId;
      if (editId) {
        await boardAPI.update(editId, { ...values, content });
      } else {
        const res = await boardAPI.create({ ...values, content });
        boardId = res.data.id;
      }
      // 첨부 업로드 — 파일별 진행률 표시 (한 개씩 업로드)
      const toUpload = pendingFiles.filter((p) => p.status === 'ready');
      for (const pf of toUpload) {
        setPendingFiles((prev) => prev.map((p) => (p.uid === pf.uid ? { ...p, status: 'uploading', percent: 0 } : p)));
        try {
          await boardAPI.uploadAttachments(boardId!, [pf.file], (e: any) => {
            const percent = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
            setPendingFiles((prev) => prev.map((p) => (p.uid === pf.uid ? { ...p, percent } : p)));
          });
          setPendingFiles((prev) => prev.map((p) => (p.uid === pf.uid ? { ...p, status: 'done', percent: 100 } : p)));
        } catch (err) {
          setPendingFiles((prev) => prev.map((p) => (p.uid === pf.uid ? { ...p, status: 'error' } : p)));
          throw err;
        }
      }
      message.success(editId ? '게시글을 수정했습니다.' : '게시글을 등록했습니다.');
      setFormOpen(false);
      fetchList();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await boardAPI.delete(id);
      message.success('게시글을 삭제했습니다.');
      setDetailOpen(false);
      fetchList();
    } catch {
      message.error('삭제에 실패했습니다.');
    }
  };

  const downloadAtt = async (att: Attachment) => {
    try {
      const res = await boardAPI.downloadAttachment(att.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = att.file_name; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch {
      message.error('다운로드에 실패했습니다.');
    }
  };

  const columns = useMemo(() => [
    {
      title: '구분', dataIndex: 'category', width: 90,
      render: (c: string, r: BoardItem) => (
        <Space size={4}>
          {r.is_pinned && <PushpinFilled style={{ color: '#f59e0b' }} />}
          <Tag color={c === '공지' ? 'red' : c === '자료실' ? 'blue' : 'default'}>{c || '일반'}</Tag>
        </Space>
      ),
    },
    {
      title: '제목', dataIndex: 'title',
      render: (t: string, r: BoardItem) => (
        <a onClick={() => openDetail(r.id)} style={{ fontWeight: r.is_pinned ? 600 : 400 }}>
          {t}
          {r.attachment_count > 0 && (
            <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
              <PaperClipOutlined /> {r.attachment_count}
            </Text>
          )}
        </a>
      ),
    },
    { title: '작성자', dataIndex: 'author_name', width: 120, responsive: ['md'] as any, render: (v: string) => v || '-' },
    {
      title: '조회', dataIndex: 'view_count', width: 70, align: 'center' as const, responsive: ['md'] as any, 
      render: (v: number) => <Text type="secondary"><EyeOutlined /> {v || 0}</Text>,
    },
    { title: '작성일', dataIndex: 'created_at', width: 140, render: fmtDate },
  ], []);

  return (
    <div style={{ padding: '4px 4px 0' }}>
      <div ref={cardWrapRef}>
      <Card
        styles={{ body: { padding: 16 } }}
        title={<Space><span style={{ fontSize: 18, fontWeight: 700 }}>게시판</span><Text type="secondary" style={{ fontSize: 13 }}>{total}건</Text></Space>}
        extra={
          <Space wrap>
            <Select
              value={category} style={{ width: 110 }} onChange={(v) => { setCategory(v); setPage(1); }}
              options={['전체', ...CATEGORIES].map((c) => ({ value: c, label: c }))}
            />
            <Input
              allowClear placeholder="제목·내용 검색" prefix={<SearchOutlined />} style={{ width: 220 }}
              value={search} onChange={(e) => setSearch(e.target.value)}
              onPressEnter={() => { setPage(1); fetchList(); }}
            />
            <Button icon={<ReloadOutlined />} onClick={fetchList} />
            {canWrite && <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>글쓰기</Button>}
          </Space>
        }
      >
        <div ref={tableWrapRef}>
        <Table
          rowKey="id" size="middle" columns={columns} dataSource={data} loading={loading}
          locale={{ emptyText: <Empty description="게시글이 없습니다" /> }}
          scroll={{ y: bodyHeight }}
          pagination={{
            current: page, pageSize, total, showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            showTotal: (t, r) => `${r[0]}–${r[1]} / 총 ${t}건`,
          }}
        />
        </div>
      </Card>
      </div>

      {/* 상세 모달 */}
      <Modal
        open={detailOpen} onCancel={() => setDetailOpen(false)} width={820} footer={null}
        title={detail ? (
          <Space>
            {detail.is_pinned && <PushpinFilled style={{ color: '#f59e0b' }} />}
            <Tag color={detail.category === '공지' ? 'red' : detail.category === '자료실' ? 'blue' : 'default'}>{detail.category}</Tag>
            <span>{detail.title}</span>
          </Space>
        ) : ''}
      >
        {detail && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#888', fontSize: 13, marginBottom: 12, borderBottom: '1px solid #f0f0f0', paddingBottom: 8 }}>
              <span>작성자 {detail.author_name || '-'}</span>
              <Space size={16}>
                <span><EyeOutlined /> {detail.view_count}</span>
                <span>{fmtDate(detail.created_at)}</span>
              </Space>
            </div>

            <div
              className="rte-editable"
              style={{ minHeight: 120, fontSize: 14, lineHeight: 1.7 }}
              dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(detail.content) || '<p style="color:#bbb">내용 없음</p>' }}
            />

            {detail.attachments?.length > 0 && (
              <div style={{ marginTop: 20, padding: 12, background: '#fafafa', borderRadius: 8 }}>
                <Text strong style={{ fontSize: 13 }}><PaperClipOutlined /> 첨부파일 {detail.attachments.length}개</Text>
                <div style={{ marginTop: 8 }}>
                  {detail.attachments.map((a) => (
                    <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                      <a onClick={() => downloadAtt(a)}><DownloadOutlined /> {a.file_name}</a>
                      <Text type="secondary" style={{ fontSize: 12 }}>{fmtSize(a.file_size)}</Text>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginTop: 20, textAlign: 'right' }}>
              <Space>
                {canWrite && <Button icon={<EditOutlined />} onClick={() => openEdit(detail)}>수정</Button>}
                {canDelete && (
                  <Popconfirm title="이 게시글을 삭제할까요?" okText="삭제" cancelText="취소" okButtonProps={{ danger: true }} onConfirm={() => handleDelete(detail.id)}>
                    <Button danger icon={<DeleteOutlined />}>삭제</Button>
                  </Popconfirm>
                )}
              </Space>
            </div>
          </div>
        )}
      </Modal>

      {/* 작성/수정 모달 */}
      <Modal
        open={formOpen} onCancel={() => setFormOpen(false)} width={840}
        title={editId ? '게시글 수정' : '새 게시글'}
        okText={editId ? '수정' : '등록'} cancelText="취소"
        confirmLoading={saving} onOk={handleSave} maskClosable={false}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Space style={{ width: '100%' }} align="end">
            <Form.Item name="category" label="구분" style={{ width: 140 }}>
              <Select options={CATEGORIES.map((c) => ({ value: c, label: c }))} />
            </Form.Item>
            <Form.Item name="is_pinned" valuePropName="checked" style={{ marginBottom: 8 }}>
              <Checkbox>상단 고정</Checkbox>
            </Form.Item>
          </Space>
          <Form.Item name="title" label="제목" rules={[{ required: true, message: '제목을 입력하세요.' }]}>
            <Input placeholder="제목" maxLength={300} showCount />
          </Form.Item>
          <Form.Item label="내용">
            <RichTextEditor value={content} onChange={setContent} />
          </Form.Item>
          <Form.Item label={`첨부파일 (개별 최대 ${MAX_FILE_MB}MB)`}>
            {existingAtts.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                {existingAtts.map((a) => (
                  <Tag key={a.id} closable onClose={(e) => { e.preventDefault(); removeExistingAtt(a.id); }} style={{ marginBottom: 4 }}>
                    <PaperClipOutlined /> {a.file_name} ({fmtSize(a.file_size)})
                  </Tag>
                ))}
              </div>
            )}
            <Upload multiple beforeUpload={beforeUpload} showUploadList={false} fileList={[]}>
              <Button icon={<PaperClipOutlined />}>파일 선택</Button>
            </Upload>
            {pendingFiles.length > 0 && (
              <div style={{ marginTop: 10 }}>
                {pendingFiles.map((pf) => (
                  <div key={pf.uid} style={{ padding: '6px 10px', marginBottom: 6, border: '1px solid #f0f0f0', borderRadius: 6, background: '#fafafa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <PaperClipOutlined /> {pf.name}
                        <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>{fmtSize(pf.size)}</Text>
                      </span>
                      <span style={{ flexShrink: 0 }}>
                        {pf.status === 'ready' && (
                          <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => removePending(pf.uid)} />
                        )}
                        {pf.status === 'uploading' && <Text type="secondary" style={{ fontSize: 12 }}>{pf.percent}%</Text>}
                        {pf.status === 'done' && <Text type="success" style={{ fontSize: 12 }}>완료</Text>}
                        {pf.status === 'error' && <Text type="danger" style={{ fontSize: 12 }}>실패</Text>}
                      </span>
                    </div>
                    {pf.status !== 'ready' && (
                      <Progress
                        percent={pf.percent} size="small" style={{ marginBottom: 0 }}
                        status={pf.status === 'error' ? 'exception' : pf.status === 'done' ? 'success' : 'active'}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BoardPage;
